import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPlay, FiClock } from 'react-icons/fi';
import Header from '../components/layout/Header';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
import { CircularProgress, AuditScoreChart } from '../components/charts/DashboardCharts';
import { AuditResult } from '../types';
import { auditApi } from '../api/audit';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import toast from 'react-hot-toast';

const DEMO_AUDIT: AuditResult = {
    id: '1',
    company_id: '1',
    global_score: 78,
    scores: {
      conformite: 82,
      gouvernance: 71,
      dialogue_social: 68,
      fidelisation: 85,
      qvt: 84,
    },
    recommendations: [
      { id: '1', title: 'Mettre à jour le DUERP', description: 'Le document unique doit être actualisé suite aux derniers changements organisationnels', priority: 'high', category: 'conformite', status: 'pending' },
      { id: '2', title: 'Organiser les élections CSE', description: 'Le renouvellement du CSE est prévu dans les 3 prochains mois', priority: 'high', category: 'dialogue_social', status: 'pending' },
      { id: '3', title: 'Compléter les entretiens professionnels', description: '8 entretiens professionnels sont à rattraper', priority: 'medium', category: 'fidelisation', status: 'in_progress' },
      { id: '4', title: 'Affichages obligatoires', description: 'Vérifier la mise à jour des affichages réglementaires', priority: 'low', category: 'conformite', status: 'pending' },
      { id: '5', title: 'Plan de formation', description: 'Établir le plan de développement des compétences annuel', priority: 'medium', category: 'fidelisation', status: 'done' },
    ],
    gaps: [
      { area: 'Sécurité au travail', current_state: 'DUERP non à jour', target_state: 'DUERP actualisé annuellement', gap_score: 60, actions: ['Actualiser le DUERP', 'Former les managers'] },
      { area: 'Représentation du personnel', current_state: 'CSE à renouveler', target_state: 'CSE en place et fonctionnel', gap_score: 45, actions: ['Organiser les élections', 'Définir le calendrier'] },
      { area: 'Formation', current_state: 'Plan partiel', target_state: 'Plan complet validé', gap_score: 70, actions: ['Finaliser le plan', 'Budget formation'] },
    ],
    created_at: '2026-04-30T10:00:00Z',
};

const AuditPage: React.FC = () => {
  const [audit, setAudit] = useState<AuditResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('results');

  const historyData = [
    { date: 'Oct 25', score: 58 },
    { date: 'Nov 25', score: 62 },
    { date: 'Dec 25', score: 65 },
    { date: 'Jan 26', score: 70 },
    { date: 'Fév 26', score: 72 },
    { date: 'Mar 26', score: 74 },
    { date: 'Avr 26', score: 78 },
  ];

  useEffect(() => {
    const load = async () => {
      try {
        const res = await auditApi.getLatest();
        const d = res.data as any;
        setAudit(d.audit || d);
      } catch {
        setAudit(DEMO_AUDIT);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await auditApi.run();
      const d = res.data as any;
      setAudit(d.audit || d);
      toast.success('Audit terminé avec succès');
    } catch {
      toast.success('Audit terminé avec succès (demo)');
    } finally {
      setRunning(false);
    }
  };

  const pillarLabels: Record<string, { label: string; color: string }> = {
    conformite: { label: 'Conformité', color: '#2D5F8A' },
    gouvernance: { label: 'Gouvernance', color: '#1B2A4A' },
    dialogue_social: { label: 'Dialogue social', color: '#4ECDC4' },
    fidelisation: { label: 'Fidélisation', color: '#27AE60' },
    qvt: { label: 'QVT', color: '#F39C12' },
  };

  const currentAudit = audit || DEMO_AUDIT;

  const tabs = [
    { key: 'results', label: 'Résultats' },
    { key: 'recommendations', label: 'Recommandations' },
    { key: 'gaps', label: 'Analyse des écarts' },
    { key: 'history', label: 'Historique' },
  ];

  const formatAuditDate = (dateStr?: string): string => {
    if (!dateStr) return 'Non disponible';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'Non disponible';
      return format(d, 'd MMMM yyyy', { locale: fr });
    } catch {
      return 'Non disponible';
    }
  };

  if (loading) {
    return (
      <div>
        <Header title="Audit & QVT" subtitle="Évaluation de votre conformité RH" />
        <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          Chargement...
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Audit & QVT" subtitle="Évaluation de votre conformité RH" />

      <div style={{ padding: '1.5rem 2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiClock size={14} />
            Dernier audit : {formatAuditDate(currentAudit.created_at)}
          </div>
          <Button icon={<FiPlay size={16} />} onClick={handleRun} loading={running}>
            Lancer un audit
          </Button>
        </div>

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {activeTab === 'results' && (
          <div>
            {/* Global score */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <Card style={{ marginBottom: '1.5rem', textAlign: 'center', padding: '2rem' }}>
                <h3 style={{ marginBottom: '1.5rem', color: 'var(--color-text-secondary)' }}>Score global de conformité</h3>
                <CircularProgress value={currentAudit.global_score ?? 0} size={180} strokeWidth={14} label="" />
                <p style={{ marginTop: '1rem', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  {currentAudit.global_score >= 80 ? 'Bon niveau de conformité' :
                   currentAudit.global_score >= 60 ? 'Niveau acceptable, des améliorations sont nécessaires' :
                   'Attention, des actions correctives sont requises'}
                </p>
              </Card>
            </motion.div>

            {/* Pillar scores */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
              {Object.entries(currentAudit.scores || {}).map(([key, score], i) => {
                const pillar = pillarLabels[key];
                if (!pillar) return null;
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Card style={{ textAlign: 'center' }}>
                      <CircularProgress value={score ?? 0} size={100} strokeWidth={8} color={pillar.color} label={pillar.label} />
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {(currentAudit.recommendations || []).map((rec, i) => (
              <motion.div
                key={rec.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card hover>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <h4 style={{ margin: 0, fontSize: 'var(--font-size-base)' }}>{rec.title}</h4>
                        <Badge variant={rec.priority === 'high' ? 'danger' : rec.priority === 'medium' ? 'warning' : 'info'} size="sm">
                          {rec.priority === 'high' ? 'Haute' : rec.priority === 'medium' ? 'Moyenne' : 'Basse'}
                        </Badge>
                      </div>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                        {rec.description}
                      </p>
                    </div>
                    <Badge variant={rec.status === 'done' ? 'success' : rec.status === 'in_progress' ? 'info' : 'neutral'}>
                      {rec.status === 'done' ? 'Fait' : rec.status === 'in_progress' ? 'En cours' : 'À faire'}
                    </Badge>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {activeTab === 'gaps' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {(currentAudit.gaps || []).map((gap, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0 }}>{gap.area}</h4>
                    <CircularProgress value={gap.gap_score} size={56} strokeWidth={5} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{ padding: '0.75rem', background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)' }}>
                      <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-danger)', marginBottom: '0.25rem' }}>Situation actuelle</p>
                      <p style={{ fontSize: 'var(--font-size-sm)' }}>{gap.current_state}</p>
                    </div>
                    <div style={{ padding: '0.75rem', background: 'var(--color-success-light)', borderRadius: 'var(--radius-md)' }}>
                      <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-success)', marginBottom: '0.25rem' }}>Objectif</p>
                      <p style={{ fontSize: 'var(--font-size-sm)' }}>{gap.target_state}</p>
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>Actions à mener :</p>
                    <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
                      {gap.actions.map((action, j) => (
                        <li key={j} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {activeTab === 'history' && (
          <AuditScoreChart data={historyData} title="Évolution du score de conformité" />
        )}
      </div>
    </div>
  );
};

export default AuditPage;
